import React, { useCallback, useState } from 'react'
import { formatPhone } from '@@partner:helpers/util'
import { useGridHelper } from '@@partner:helpers/crud/grid'
import { useFetchAllCouriers } from '@@partner:graphql/query/employees/allCouriers.gql'

import { Link, generatePath } from 'react-router-dom'
import { Table, Tag, Pagination } from 'rsuite'
import AppLoader from '@components/common/AppLoader'
import CommonStatusLabel from '@components/common/CommonStatusLabel'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import { EditButton } from '@components/common/form/Buttons'

import { ROUTE_USERS_MANAGEMENT_EDIT } from '@@partner:config/routes'
import CouriersFilter from './CouriersFilter'
import { NestedFilter } from '@@partner:types:graphql'

const Column = Table.Column
const HeaderCell = Table.HeaderCell
const Cell = Table.Cell

export interface CouriersTableProps {
  header?: string
}

export default function CouriersTable ({ header }: CouriersTableProps) {
  const { page, tableSort, handlePageSelect, handleSortColumn } =
    useGridHelper()

  const [ filter, setFilter ] = useState<NestedFilter>({
    fields: [ 'id' ],
    op: 'unwrap'
  })

  const { data, loading, error } = useFetchAllCouriers({
    filter,
    pager: { page },
    sort: {
      direction: tableSort.direction,
      fieldName: tableSort.fieldName
    }
  })

  const handleFilterChanged = useCallback((updatedFilter: NestedFilter) => {
    setFilter(updatedFilter)
    handlePageSelect(1)
  }, [ handlePageSelect ])

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        error={error}
        title="Ошибка загрузки курьеров"
      />
    )
  }

  const couriers = data?.couriers?.edges ?? []
  const pageInfo = data?.couriers?.pageInfo

  return (
    <>
      {header && <h4 className="u-form-heading">{header}</h4>}
      <CouriersFilter onFilterChanged={handleFilterChanged} />
      <Table
        data={couriers}
        autoHeight
        wordWrap
        loading={loading}
        sortColumn={tableSort.fieldName}
        sortType={tableSort.sortType}
        onSortColumn={handleSortColumn}
        renderLoading={() =>
          loading ? <AppLoader.Inner content="Загрузка таблицы" /> : null
        }
        affixHeader
      >
        <Column sortable flexGrow={1} verticalAlign="middle">
          <HeaderCell>ИД</HeaderCell>
          <Cell dataKey="id" />
        </Column>

        <Column sortable flexGrow={2} verticalAlign="middle">
          <HeaderCell>ФИО</HeaderCell>
          <Cell dataKey="first_name">
            {(courier) => <span>{courier.fullName}</span>}
          </Cell>
        </Column>

        <Column sortable flexGrow={2} verticalAlign="middle">
          <HeaderCell>Телефон</HeaderCell>
          <Cell dataKey="phone">
            {(courier) => <Tag color="blue">{formatPhone(courier.phone)}</Tag>}
          </Cell>
        </Column>

        <Column sortable flexGrow={2} verticalAlign="middle">
          <HeaderCell>Статус</HeaderCell>
          <Cell dataKey="status">
            {(courier) => <CommonStatusLabel status={courier.status} />}
          </Cell>
        </Column>

        <Column flexGrow={2} fixed="right" verticalAlign="middle">
          <HeaderCell>Управление</HeaderCell>
          <Cell>
            {(courier) => (
              <EditButton
                as={Link}
                to={generatePath(ROUTE_USERS_MANAGEMENT_EDIT, {
                  type: 'couriers',
                  id: courier.id
                })}
                size="xs"
              />
            )}
          </Cell>
        </Column>
      </Table>
      <Pagination
        last
        first
        next
        prev
        ellipsis
        activePage={page}
        pages={pageInfo?.lastPage}
        maxButtons={10}
        total={pageInfo?.total ?? 0}
        onSelect={handlePageSelect}
      />
    </>
  )
}
